/**
 * turnosFijos.js
 *
 * Sub-fase 6.B · Helpers puros compartidos para selección y validación
 * de turnos fijos de clases fijas.
 *
 * ⚠️ NO se integra todavía en ningún modal · sólo expuesto para tests
 * unitarios. La integración real entra en 6.C (modal inscripción) y 6.D
 * (pestaña inscripción) con autorización explícita Ann.
 *
 * Recupera y unifica la lógica buena que vive ya en:
 *   - frontend/src/components/admin/ClientModal.jsx
 *   - frontend/src/pages/ClientNewProfile.jsx (pestaña Inscripción)
 *
 * Sin imports de React. Sin axios. Sin acceso a BD. 100% funciones puras.
 *
 * Códigos de error tipados (alineados con backend Sub-fase 6.A):
 *   CUOTA_HORAS_SEMANA_NO_CONFIGURADA
 *   TURNO_INCOMPLETO
 *   TURNO_NO_COINCIDE_CUOTA
 *   TURNO_DUPLICADO
 *   TURNO_DIA_REPETIDO        (modo opcional · bloquea sólo si configurado)
 */

// ──────────────────────────────────────────────────────────────────────────
// Constantes
// ──────────────────────────────────────────────────────────────────────────

export const CODIGOS_ERROR = Object.freeze({
  CUOTA_HORAS_SEMANA_NO_CONFIGURADA: "CUOTA_HORAS_SEMANA_NO_CONFIGURADA",
  TURNO_INCOMPLETO: "TURNO_INCOMPLETO",
  TURNO_NO_COINCIDE_CUOTA: "TURNO_NO_COINCIDE_CUOTA",
  TURNO_DUPLICADO: "TURNO_DUPLICADO",
  TURNO_DIA_REPETIDO: "TURNO_DIA_REPETIDO",
});

export const REGLA_DIAS = Object.freeze({
  PERMITIR: "permitir",   // sin validación · admin libre
  WARNING: "warning",     // detecta y avisa, no bloquea
  BLOCKING: "blocking",   // detecta y bloquea
});

// ──────────────────────────────────────────────────────────────────────────
// Helpers internos
// ──────────────────────────────────────────────────────────────────────────

function _esHorasSemanaValida(v) {
  // STRICT: sólo number entero positivo. Strings como "2" se RECHAZAN
  // intencionalmente para forzar al backend/UI a normalizar antes.
  return typeof v === "number" && Number.isInteger(v) && v >= 1;
}

function _normalizarString(v) {
  return typeof v === "string" ? v.trim() : "";
}

// ──────────────────────────────────────────────────────────────────────────
// 1. calcularNumeroTurnosPorCuota
// ──────────────────────────────────────────────────────────────────────────

/**
 * Devuelve `cuota.horas_semana` si es entero >= 1.
 * Devuelve null en cualquier otro caso (cuota null, sin campo, "2" string,
 * 0, negativo, NaN, etc).
 *
 * STRICT por diseño: rechaza strings como "2" para evitar que el frontend
 * acepte cuotas con formato sucio. El backend (6.A) ya devuelve `int|null`
 * limpio en /admin/cuotas, así que strings sólo aparecen si alguien
 * mete datos manualmente.
 */
export function calcularNumeroTurnosPorCuota(cuota) {
  if (!cuota || typeof cuota !== "object") return null;
  const v = cuota.horas_semana;
  return _esHorasSemanaValida(v) ? v : null;
}

// ──────────────────────────────────────────────────────────────────────────
// 2. ajustarTurnosAHorasSemana
// ──────────────────────────────────────────────────────────────────────────

/**
 * Devuelve un array de exactamente `n` turnos.
 *  - Conserva los primeros `n` del input.
 *  - Rellena con {dia:'', hora:''} si faltan.
 *  - NO muta el input.
 *
 *   ajustarTurnosAHorasSemana([], 3)               // 3 vacíos
 *   ajustarTurnosAHorasSemana([t1, t2], 3)          // [t1, t2, vacío]
 *   ajustarTurnosAHorasSemana([t1, t2, t3], 2)     // [t1, t2]
 *
 *   n null/0/negativo → devuelve [] (vacío seguro).
 */
export function ajustarTurnosAHorasSemana(turnos, n) {
  if (typeof n !== "number" || !Number.isInteger(n) || n < 1) return [];
  const fuente = Array.isArray(turnos) ? turnos : [];
  const out = fuente.slice(0, n).map((t) => ({
    dia: t && typeof t === "object" ? t.dia ?? "" : "",
    hora: t && typeof t === "object" ? t.hora ?? "" : "",
  }));
  while (out.length < n) {
    out.push({ dia: "", hora: "" });
  }
  return out;
}

// ──────────────────────────────────────────────────────────────────────────
// 3. normalizarTurnoFijo
// ──────────────────────────────────────────────────────────────────────────

/**
 * Devuelve array con {dia, hora} normalizados (trim, strings).
 * No inventa valores: si llega null/undefined/no-string devuelve "".
 * No filtra · conserva longitud y orden.
 */
export function normalizarTurnoFijo(turnos) {
  const fuente = Array.isArray(turnos) ? turnos : [];
  return fuente.map((t) => ({
    dia: _normalizarString(t && typeof t === "object" ? t.dia : ""),
    hora: _normalizarString(t && typeof t === "object" ? t.hora : ""),
  }));
}

// ──────────────────────────────────────────────────────────────────────────
// 4. validarTurnosCompletos
// ──────────────────────────────────────────────────────────────────────────

/**
 * Detecta turnos incompletos (día o hora vacíos).
 * Devuelve { ok, incompletos: [{indice, falta:["dia"|"hora"]}] }.
 * indice es 0-based (la UI lo formatea +1 si quiere).
 */
export function validarTurnosCompletos(turnos) {
  const norm = normalizarTurnoFijo(turnos);
  const incompletos = [];
  norm.forEach((t, idx) => {
    const falta = [];
    if (!t.dia) falta.push("dia");
    if (!t.hora) falta.push("hora");
    if (falta.length > 0) incompletos.push({ indice: idx, falta });
  });
  return { ok: incompletos.length === 0, incompletos };
}

// ──────────────────────────────────────────────────────────────────────────
// 5. validarTurnosExactos
// ──────────────────────────────────────────────────────────────────────────

/**
 * Valida que hay exactamente `n` turnos COMPLETOS.
 *  - len(turnos completos) === n.
 *  - Devuelve { ok, code?, detail: { turnos_requeridos, turnos_recibidos } }.
 *
 * NO valida duplicados ni completitud individual: combina con los otros
 * helpers para una validación completa.
 */
export function validarTurnosExactos(turnos, n) {
  const norm = normalizarTurnoFijo(turnos);
  const completos = norm.filter((t) => t.dia && t.hora);
  const detail = {
    turnos_requeridos: n,
    turnos_recibidos: completos.length,
  };
  if (typeof n !== "number" || !Number.isInteger(n) || n < 1) {
    return {
      ok: false,
      code: CODIGOS_ERROR.CUOTA_HORAS_SEMANA_NO_CONFIGURADA,
      detail: { ...detail, razon: "horas_semana no configurada" },
    };
  }
  if (completos.length !== n) {
    return {
      ok: false,
      code: CODIGOS_ERROR.TURNO_NO_COINCIDE_CUOTA,
      detail: {
        ...detail,
        razon: `La membresía requiere ${n} horarios y se han enviado ${completos.length}.`,
      },
    };
  }
  return { ok: true, detail };
}

// ──────────────────────────────────────────────────────────────────────────
// 6. validarNoDuplicarDiaHora
// ──────────────────────────────────────────────────────────────────────────

/**
 * Detecta pares (dia, hora) repetidos exactos.
 * Devuelve { ok, repetidos: [{dia, hora, veces, indices:[...]}] }.
 * Sólo cuenta turnos completos.
 */
export function validarNoDuplicarDiaHora(turnos) {
  const norm = normalizarTurnoFijo(turnos);
  const conteo = new Map(); // key → { veces, indices: [] }
  norm.forEach((t, idx) => {
    if (!t.dia || !t.hora) return;
    const key = `${t.dia}#${t.hora}`;
    const entry = conteo.get(key) || { dia: t.dia, hora: t.hora, indices: [] };
    entry.indices.push(idx);
    conteo.set(key, entry);
  });
  const repetidos = [];
  for (const e of conteo.values()) {
    if (e.indices.length > 1) {
      repetidos.push({
        dia: e.dia, hora: e.hora,
        veces: e.indices.length, indices: [...e.indices],
      });
    }
  }
  return { ok: repetidos.length === 0, repetidos };
}

// ──────────────────────────────────────────────────────────────────────────
// 7. validarNoRepetirDia
// ──────────────────────────────────────────────────────────────────────────

/**
 * Detecta días que aparecen en >1 turno (con horas distintas o iguales).
 * Devuelve { ok, dias_repetidos: [{dia, veces, indices}], modo, bloquea }.
 *
 *   modo ∈ { 'permitir' | 'warning' | 'blocking' } · default 'warning'.
 *   - 'permitir': nunca avisa ni bloquea · ok=true siempre
 *   - 'warning':  detecta, ok=false, pero bloquea=false (sólo aviso UI)
 *   - 'blocking': detecta, ok=false, bloquea=true
 *
 * NOTA Ann (decisión 6.B): la regla funcional definitiva está pendiente
 * de confirmar (¿clases mañana+tarde mismo día son válidas en Continuo 8?).
 * Por defecto warning para que el admin vea la incidencia sin bloqueo.
 */
export function validarNoRepetirDia(turnos, modo = REGLA_DIAS.WARNING) {
  const norm = normalizarTurnoFijo(turnos);
  if (modo === REGLA_DIAS.PERMITIR) {
    return { ok: true, dias_repetidos: [], modo, bloquea: false };
  }
  const conteo = new Map();
  norm.forEach((t, idx) => {
    if (!t.dia) return;
    const e = conteo.get(t.dia) || { dia: t.dia, indices: [] };
    e.indices.push(idx);
    conteo.set(t.dia, e);
  });
  const dias_repetidos = [];
  for (const e of conteo.values()) {
    if (e.indices.length > 1) {
      dias_repetidos.push({
        dia: e.dia, veces: e.indices.length, indices: [...e.indices],
      });
    }
  }
  const hay = dias_repetidos.length > 0;
  return {
    ok: !hay,
    dias_repetidos,
    modo,
    bloquea: hay && modo === REGLA_DIAS.BLOCKING,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// 7-bis. detectarFilasPerdidasAlAjustar
// ──────────────────────────────────────────────────────────────────────────

/**
 * Sub-fase 6.C · ajuste Ann: detecta filas con CONTENIDO que se perderían
 * si ajustáramos `turnos` a longitud `horasSemanaNueva`.
 *
 * Devuelve { hayPerdida, filasPerdidas: [{indice, dia, hora}] }.
 *
 * Sólo cuenta filas con día Y hora rellenos (parcialmente rellenas no
 * cuentan · si el admin sólo había puesto día y no hora aún, es ruido
 * normal del flujo de edición).
 *
 * Pura · no muta. Pensado para que el componente emita un callback al
 * padre antes de truncar: "se eliminará Viernes 10:00".
 */
export function detectarFilasPerdidasAlAjustar(turnos, horasSemanaNueva) {
  if (typeof horasSemanaNueva !== "number"
      || !Number.isInteger(horasSemanaNueva)
      || horasSemanaNueva < 0) {
    return { hayPerdida: false, filasPerdidas: [] };
  }
  const fuente = Array.isArray(turnos) ? turnos : [];
  if (fuente.length <= horasSemanaNueva) {
    return { hayPerdida: false, filasPerdidas: [] };
  }
  const filasPerdidas = [];
  for (let i = horasSemanaNueva; i < fuente.length; i++) {
    const t = fuente[i];
    if (!t || typeof t !== "object") continue;
    const dia = _normalizarString(t.dia);
    const hora = _normalizarString(t.hora);
    if (dia && hora) {
      filasPerdidas.push({ indice: i, dia, hora });
    }
  }
  return { hayPerdida: filasPerdidas.length > 0, filasPerdidas };
}


// ──────────────────────────────────────────────────────────────────────────
// 8. construirPayloadTurnoFijo
// ──────────────────────────────────────────────────────────────────────────

/**
 * Construye el array {dia, hora} listo para mandar al backend.
 *  - Lanza error si hay incompletos, duplicados (dia,hora), o len != n.
 *  - Si reglaDiasDistintos === 'blocking' y hay día repetido, también lanza.
 *  - El error es objeto Error con propiedades:
 *      .code   = CODIGOS_ERROR.*
 *      .detail = objeto estructurado con razon + datos
 *
 * opciones = {
 *   horasSemana: int requerido,
 *   reglaDiasDistintos?: 'permitir'|'warning'|'blocking' (default 'warning'),
 *   becado?: bool (default false),
 * }
 *
 * PR-BECA (Ann 2026-07-06): con `becado: true` la regla de cantidad se RELAJA
 * a `1 <= nº turnos completos <= horasSemana` (mínimo 1, máximo las horas de la
 * cuota). Se ignoran las filas totalmente vacías (el modal muestra hasta
 * horasSemana filas y el becado puede dejar algunas sin rellenar). Filas
 * parcialmente rellenas (sólo día o sólo hora) siguen siendo error. El resto
 * de reglas (duplicados, día repetido) se mantiene. SIN becado el comportamiento
 * es idéntico al anterior (longitud exacta == horasSemana).
 *
 * Devuelve: array de turnos normalizados (longitud `horasSemana` en modo normal;
 * 1..horasSemana en modo becado).
 */
export function construirPayloadTurnoFijo(turnos, opciones = {}) {
  const {
    horasSemana,
    reglaDiasDistintos = REGLA_DIAS.WARNING,
    becado = false,
  } = opciones;
  const norm = normalizarTurnoFijo(turnos);

  // 1. horas_semana válida
  if (!_esHorasSemanaValida(horasSemana)) {
    const err = new Error("horas_semana no configurada");
    err.code = CODIGOS_ERROR.CUOTA_HORAS_SEMANA_NO_CONFIGURADA;
    err.detail = {
      razon: "La membresía seleccionada no tiene horas_semana configuradas.",
    };
    throw err;
  }

  // ── PR-BECA · rama becado: 1..horasSemana turnos completos ────────────
  if (becado) {
    // Descartar filas totalmente vacías (slot no usado por el becado).
    const noVacias = norm.filter((t) => t.dia || t.hora);
    // Filas parciales (sólo día o sólo hora) → incompletas.
    const compl = validarTurnosCompletos(noVacias);
    if (!compl.ok) {
      const idx1 = compl.incompletos.map((c) => c.indice + 1);
      const plural = idx1.length === 1 ? "el turno" : "los turnos";
      const err = new Error(`turnos incompletos: ${idx1.join(", ")}`);
      err.code = CODIGOS_ERROR.TURNO_INCOMPLETO;
      err.detail = {
        turnos_incompletos: idx1,
        razon: `Completa el día y la hora de ${plural} ${idx1.join(", ")}.`,
      };
      throw err;
    }
    if (noVacias.length < 1 || noVacias.length > horasSemana) {
      const err = new Error(
        `Un alta becada admite entre 1 y ${horasSemana} horarios.`);
      err.code = CODIGOS_ERROR.TURNO_NO_COINCIDE_CUOTA;
      err.detail = {
        turnos_requeridos: horasSemana,
        turnos_recibidos: noVacias.length,
        becado: true,
        razon: (`Un alta becada admite entre 1 y ${horasSemana} `
                + `horarios y se han enviado ${noVacias.length}.`),
      };
      throw err;
    }
    // Duplicados (dia, hora)
    const dupB = validarNoDuplicarDiaHora(noVacias);
    if (!dupB.ok) {
      const muestra = dupB.repetidos
        .map((r) => `${r.dia} ${r.hora} (x${r.veces})`)
        .join(", ");
      const err = new Error(`duplicados: ${muestra}`);
      err.code = CODIGOS_ERROR.TURNO_DUPLICADO;
      err.detail = {
        repetidos: dupB.repetidos,
        razon: `No puedes repetir el mismo horario. Repetidos: ${muestra}.`,
      };
      throw err;
    }
    // Día repetido (sólo bloquea si blocking)
    const diasB = validarNoRepetirDia(noVacias, reglaDiasDistintos);
    if (diasB.bloquea) {
      const muestra = diasB.dias_repetidos
        .map((r) => `${r.dia} (x${r.veces})`)
        .join(", ");
      const err = new Error(`días repetidos: ${muestra}`);
      err.code = CODIGOS_ERROR.TURNO_DIA_REPETIDO;
      err.detail = {
        dias_repetidos: diasB.dias_repetidos,
        razon: `No puedes repetir el mismo día. Repetidos: ${muestra}.`,
      };
      throw err;
    }
    return noVacias.map((t) => ({ dia: t.dia, hora: t.hora }));
  }

  // 2. Ajustar a longitud horasSemana exacta:
  //    - si llegan más, recorta (los excedentes se ignoran intencionalmente
  //      porque la UI ya no los muestra).
  //    - si llegan menos, extiende con vacíos para que validarTurnosCompletos
  //      detecte qué fila falta (mensaje accionable al admin: "completa el
  //      turno N").
  const slice = ajustarTurnosAHorasSemana(norm, horasSemana);

  // 3. Completitud: cada fila con día y hora
  const compl = validarTurnosCompletos(slice);
  if (!compl.ok) {
    const idx1 = compl.incompletos.map((c) => c.indice + 1);
    const plural = idx1.length === 1 ? "el turno" : "los turnos";
    const err = new Error(`turnos incompletos: ${idx1.join(", ")}`);
    err.code = CODIGOS_ERROR.TURNO_INCOMPLETO;
    err.detail = {
      turnos_incompletos: idx1,
      razon: `Completa el día y la hora de ${plural} ${idx1.join(", ")}.`,
    };
    throw err;
  }

  // 4. Cantidad exacta
  const exactos = validarTurnosExactos(slice, horasSemana);
  if (!exactos.ok) {
    const err = new Error(exactos.detail.razon);
    err.code = exactos.code;
    err.detail = exactos.detail;
    throw err;
  }

  // 5. Duplicados (dia, hora)
  const dup = validarNoDuplicarDiaHora(slice);
  if (!dup.ok) {
    const muestra = dup.repetidos
      .map((r) => `${r.dia} ${r.hora} (x${r.veces})`)
      .join(", ");
    const err = new Error(`duplicados: ${muestra}`);
    err.code = CODIGOS_ERROR.TURNO_DUPLICADO;
    err.detail = {
      repetidos: dup.repetidos,
      razon: `No puedes repetir el mismo horario. Repetidos: ${muestra}.`,
    };
    throw err;
  }

  // 6. Día repetido (sólo bloquea si reglaDiasDistintos === blocking)
  const dias = validarNoRepetirDia(slice, reglaDiasDistintos);
  if (dias.bloquea) {
    const muestra = dias.dias_repetidos
      .map((r) => `${r.dia} (x${r.veces})`)
      .join(", ");
    const err = new Error(`días repetidos: ${muestra}`);
    err.code = CODIGOS_ERROR.TURNO_DIA_REPETIDO;
    err.detail = {
      dias_repetidos: dias.dias_repetidos,
      razon: `No puedes repetir el mismo día. Repetidos: ${muestra}.`,
    };
    throw err;
  }

  // 7. Devolver payload limpio · sólo {dia, hora}
  return slice.map((t) => ({ dia: t.dia, hora: t.hora }));
}
